import { axiosInstance } from '../utils/axios';
import SecureStorage from '../utils/secureStorage';
import { PATHS } from '../routes/routes';

export interface LoyaltyBalance {
  totalPoints: number;
  availablePoints: number;
  redeemedPoints: number;
}

export interface PointsTransaction {
  id: string;
  transactionType: 'EARNED' | 'REDEEMED';
  points: number;
  description: string;
  createdAt: string;
  relatedPaymentId?: string;
  relatedRewardId?: string;
}

export interface Reward {
  id: string;
  title: string;
  description: string;
  requiredPoints: number;
  imageUrl?: string;
  category?: string;
  available: boolean;
  quantity: number;
  validFrom?: string;
  validUntil?: string;
}

export interface RedeemResponse {
  success: boolean;
  message: string;
  data: {
    reward: {
      id: string;
      title: string;
      description: string;
      requiredPoints: number;
    };
    remainingPoints: number;
  };
}

export interface CustomerRedemptionRequest {
  id: string;
  status: 'PENDING' | 'ACCEPTED' | 'REJECTED' | 'DELIVERED';
  pointsRequired: number;
  description: string;
  rejectionReason?: string;
  createdAt: string;
  processedAt?: string;
  deliveredAt?: string;
  reward: {
    id: string;
    title: string;
    description: string;
    category: string;
  };
}


// Get customer loyalty points balance (requires authentication)
export const getLoyaltyBalance = async (): Promise<LoyaltyBalance> => {
  const token = SecureStorage.getCustomerToken();
  if (!token) {
    throw new Error('Customer not authenticated - please sign in again');
  }
  
  try {
    const response = await axiosInstance.get('/public/loyalty/points/balance', {
      headers: {
        Authorization: token
      }
    });
    return response.data.data;
  } catch (error: any) {
    if (error.response?.status === 401) {
      SecureStorage.removeCustomerToken();
      window.location.href = PATHS.CUSTOMER_SIGNIN;
      throw new Error('Unauthorized - redirecting to sign in');
    }
    throw error;
  }
};

// Get customer points transaction history (requires authentication)
export const getPointsHistory = async (page: number = 1, limit: number = 20) => {
  const token = SecureStorage.getCustomerToken();
  if (!token) {
    throw new Error('Customer not authenticated - please sign in again');
  }
  
  try {
    const response = await axiosInstance.get(`/public/loyalty/points/history?page=${page}&limit=${limit}`, {
      headers: {
        Authorization: token
      }
    });
    return response.data.data;
  } catch (error: any) {
    if (error.response?.status === 401) {
      SecureStorage.removeCustomerToken();
      window.location.href = PATHS.CUSTOMER_SIGNIN;
      throw new Error('Unauthorized - redirecting to sign in');
    }
    throw error;
  }
};

// Get rewards catalogue
export const getRewardsCatalogue = async (): Promise<Reward[]> => {
  const response = await axiosInstance.get('/public/loyalty/rewards');
  return response.data.data;
};

// Redeem a reward (requires authentication)
export const redeemReward = async (rewardId: string): Promise<RedeemResponse> => {
  const token = SecureStorage.getCustomerToken();
  if (!token) {
    throw new Error('Customer not authenticated - please sign in again');
  }
  
  try {
    const response = await axiosInstance.post('/public/loyalty/rewards/redeem', {
      rewardId
    }, {
      headers: {
        Authorization: token
      }
    });
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 401) {
      SecureStorage.removeCustomerToken();
      window.location.href = PATHS.CUSTOMER_SIGNIN;
      throw new Error('Unauthorized - redirecting to sign in');
    }
    throw error;
  }
};

// Get customer redemption requests (requires authentication)
export const getCustomerRedemptionRequests = async (page: number = 1, limit: number = 20) => {
  const token = SecureStorage.getCustomerToken();
  if (!token) {
    throw new Error('Customer not authenticated - please sign in again');
  }
  
  try {
    const response = await axiosInstance.get(`/public/loyalty/redemption-requests?page=${page}&limit=${limit}`, {
      headers: {
        Authorization: token
      }
    });
    return response.data.data;
  } catch (error: any) {
    if (error.response?.status === 401) {
      SecureStorage.removeCustomerToken();
      window.location.href = PATHS.CUSTOMER_SIGNIN;
      throw new Error('Unauthorized - redirecting to sign in');
    }
    throw error;
  }
};
